import { api } from '@/services';
import { is401, getErrorDetails } from '@/utils';

const CLIENT_ID = import.meta.env.VITE_SPOTIFY_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_SPOTIFY_REDIRECT_URI;

// handleCallback runs on the redirect-back page and then navigates away, so it
// can't touch auth state directly; it stashes the notice here for refresh()
// to surface on the converter page. Deliberately generic — never reveal
// why Spotify rejected the sign-in.
const AUTH_ERROR_STORAGE_KEY = 'spotify_auth_error';
const AUTH_ERROR_MESSAGE =
  "We're experiencing problems with Spotify authentication. We're working on it.";

const SCOPES = [
  'playlist-read-private',
  'playlist-modify-public',
  'playlist-modify-private',
  'playlist-read-collaborative',
].join(' ');

export const login = async () => {
  const state = crypto.randomUUID();

  sessionStorage.setItem('spotify_auth_state', state);
  sessionStorage.setItem(
    'oauth_return_path',
    window.location.pathname + window.location.search
  );

  const authParams = new URLSearchParams({
    state,
    scope: SCOPES,
    show_dialog: 'true',
    client_id: CLIENT_ID,
    response_type: 'code',
    redirect_uri: REDIRECT_URI,
  });

  window.location.href = `https://accounts.spotify.com/authorize?${authParams.toString()}`;
};

export const handleCallback = async (searchParams) => {
  const error = searchParams.get('error');

  if (error) {
    console.error('Spotify auth error:', {
      error,
      error_description: searchParams.get('error_description'),
    });
    return;
  }

  const code = searchParams.get('code');
  const returnedState = searchParams.get('state');
  const storedState = sessionStorage.getItem('spotify_auth_state');

  sessionStorage.removeItem('spotify_auth_state');

  if (returnedState !== storedState) {
    console.error('Authorization state mismatch.');
    return;
  }

  try {
    await api.exchangeSpotifyCode(code);
  } catch (error) {
    // The server returns 403 when Spotify rejects the sign-in (expired/reused
    // code, or an account it won't grant). Stash a generic notice for
    // refresh() to surface; rethrow anything else so it
    // stays a real error.
    const { is403 } = getErrorDetails(error);

    if (!is403) throw error;

    sessionStorage.setItem(AUTH_ERROR_STORAGE_KEY, AUTH_ERROR_MESSAGE);
  }
};

export const refresh = async (updateAuthState, onUnauthorized) => {
  updateAuthState({ isSigningIn: true });

  // Surface (once) any notice left by a failed sign-in, then clear it so it
  // doesn't linger across later refreshes.
  const authError = sessionStorage.getItem(AUTH_ERROR_STORAGE_KEY);

  if (authError) sessionStorage.removeItem(AUTH_ERROR_STORAGE_KEY);

  try {
    const accounts = await api.getSpotifyStatus();

    updateAuthState({ accounts, isSigningIn: false, error: authError || null });
  } catch (error) {
    const { is429, message } = getErrorDetails(error);

    if (is429) {
      updateAuthState({ isSigningIn: false, error: message });
      return;
    }

    updateAuthState({
      accounts: [],
      isSigningIn: false,
      error: authError || null,
    });

    if (is401(error)) onUnauthorized();
  }
};

export const logout = async (updateAuthState, onUnauthorized, accountId) => {
  updateAuthState({ isSigningOut: true });

  try {
    await api.disconnectSpotifyAccount(accountId);
  } catch (error) {
    if (is401(error)) onUnauthorized();
  } finally {
    await refresh(updateAuthState, onUnauthorized);

    updateAuthState({ isSigningOut: false });
  }
};
